/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Badge, Tag, Empty, Tooltip, Skeleton } from 'antd';
import ResponsiveBreadcrumb from '../../../components/breadcrumb/ResponsiveBreadcrumb';
import {
   BookOutlined, CheckCircleOutlined, ClockCircleOutlined, RollbackOutlined, RightOutlined
} from '@ant-design/icons';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import { listStudentTaskSubjectGroups } from '../../../services/MicrocredentialsStudentTaskService';
import '../../common/BraineeModule.css';

function toArray(payload) {
   if (Array.isArray(payload)) {
      return payload;
   }

   if (Array.isArray(payload?.data)) {
      return payload.data;
   }

   if (Array.isArray(payload?.items)) {
      return payload.items;
   }

   return [];
}

export function StudentPerformanceTaskSubjects({ mode = detectMicrocredentialsTaskMode(window.location?.pathname) } = {}) {
   const navigate = useNavigate();
   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;
   const taskLabelPlural = moduleConfig.labelPlural;

   const [subjectList, setSubjectList] = useState([]);
   const [userId] = useState(Utils.getCurrentUserInfo(Enums.UserInfo.id));
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      Utils.verifyJwt();
      getSubjects(userId);
   }, [userId]);

   const getSubjects = async (userid) => {
      try {
         setLoading(true);
         const response = await listStudentTaskSubjectGroups({ id: userid }, mode);
         const data = toArray(response);
         const subjects = data.map((item) => ({
            value: item.value,
            name: item.name,
            submitted: item.submitted,
            not_submitted: item.not_submitted,
            returned: item.returned,
         }));
         setSubjectList(subjects);
      } catch (error) {
         setSubjectList([]);
      } finally {
         setLoading(false);
      }
   };

   const itemSubjectClick = (subject, subjectname) => {
      navigate(`${moduleConfig.studentListRoute}?s=${subject}&sn=${subjectname}`);
   };

   const totalNotSubmitted = subjectList.reduce((sum, item) => sum + Number(item.not_submitted || 0), 0);
   const totalSubmitted = subjectList.reduce((sum, item) => sum + Number(item.submitted || 0), 0);
   const totalReturned = subjectList.reduce((sum, item) => sum + Number(item.returned || 0), 0);

   const renderSubjectSkeletons = () => (
      <div className="brainee-module-skeleton-grid">
         {Array.from({ length: 6 }).map((_, index) => (
            <div className="brainee-module-card is-skeleton" key={`student-performance-task-subject-skeleton-${index}`}>
               <Skeleton.Avatar active shape="square" className="brainee-module-card-skeleton-icon" />
               <div className="brainee-module-card-copy">
                  <Skeleton.Input active size="small" className="brainee-module-card-skeleton-title" />
                  <div className="brainee-module-card-skeleton-badges">
                     <Skeleton.Button active size="small" className="brainee-module-card-skeleton-badge" />
                     <Skeleton.Button active size="small" className="brainee-module-card-skeleton-badge" />
                  </div>
               </div>
               <Skeleton.Button active size="small" shape="circle" className="brainee-module-card-skeleton-action" />
            </div>
         ))}
      </div>
   );

   return (
      <div className="dashboard-area-all brainee-module-page">
         <div className="dashboard-item brainee-module-shell">
            <div className="brainee-module-breadcrumb">
               <ResponsiveBreadcrumb
                  items={[
                     { key: 'home', node: <Link to="/home">Home</Link> },
                     { key: 'mc', node: 'Brainee Microcredentials' },
                     { key: 'pt', node: taskLabel },
                  ]}
               />
            </div>

            <section className="brainee-module-hero">
               <div>
                  <span className="brainee-module-kicker">{taskLabel}</span>
                  <h1>{moduleConfig.myLabel}</h1>
                  <p>Open a course to view pending, submitted, and returned {taskLabelPlural.toLowerCase()} in one place.</p>
                  <div className="brainee-module-meta">
                     <span><ClockCircleOutlined /> {loading ? 'Loading' : totalNotSubmitted} not submitted</span>
                     <span><CheckCircleOutlined /> {loading ? 'Loading' : totalSubmitted} submitted</span>
                     <span><RollbackOutlined /> {loading ? 'Loading' : totalReturned} returned</span>
                  </div>
               </div>
            </section>

            <section className="brainee-module-panel">
               <div className="brainee-module-panel-header">
                  <div>
                     <span className="brainee-module-kicker">Browse</span>
                     <h2>Course Groups</h2>
                  </div>
                  <span className="brainee-module-count">{loading ? '...' : subjectList.length}</span>
               </div>

               {loading
                  ? renderSubjectSkeletons()
                  : subjectList.length > 0
                     ? <div className="brainee-module-grid">
                        {subjectList.map((subject) => (
                           <button className="brainee-module-card" key={subject.value} type="button" onClick={() => itemSubjectClick(subject.value, subject.name)}>
                              <span className="brainee-module-card-icon"><BookOutlined /></span>
                              <span className="brainee-module-card-copy">
                                 <Tooltip placement="top" title={subject.name}>
                                    <span className="brainee-module-card-title">{subject.name}</span>
                                 </Tooltip>
                                 <span className="brainee-module-card-badges">
                                    <Badge count={subject.not_submitted} style={{ backgroundColor: 'gold' }} overflowCount={999}>
                                       <Tag color="gold">Not Submitted</Tag>
                                    </Badge>
                                    <Badge count={subject.submitted} style={{ backgroundColor: 'green' }} overflowCount={999}>
                                       <Tag color="green">Submitted</Tag>
                                    </Badge>
                                    <Badge count={subject.returned} style={{ backgroundColor: 'red' }} overflowCount={999}>
                                       <Tag color="red">Returned</Tag>
                                    </Badge>
                                 </span>
                              </span>
                              <RightOutlined className="brainee-module-card-arrow" />
                           </button>
                        ))}
                     </div>
                     : <div className="brainee-module-state">
                        <Empty image="../../images/brainee_mascot.png" description="No records found." />
                     </div>}
            </section>
         </div>
      </div>
   );
}
